import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import api from '../../services/api';
import axios from 'axios';

export interface User {
    id: string;
    name: string;
    deviceId: string;
    createdAt: string;
    updatedAt: string;
    role?: string;
}

interface UserState {
    currentUser: User | null;
    loading: boolean;
    error: string | null;
}

const initialState: UserState = {
    currentUser: null,
    loading: false,
    error: null
};

export const initializeUser = createAsyncThunk(
    'user/initialize',
    async (_, { rejectWithValue }) => {
        let deviceId = localStorage.getItem('deviceId');
        console.log('[userSlice] deviceId:', deviceId);
        if (!deviceId) {
            deviceId = uuidv4();
            localStorage.setItem('deviceId', deviceId);
        }
        
        try {
            // Attempt to fetch user from backend first
            console.log(`[userSlice] Initializing user with deviceId: ${deviceId}`);
            const response = await api.get<User>(`/users/device/${deviceId}`);
            console.log('[userSlice] Backend response for user by deviceId:', response.data);
            localStorage.setItem(`user_${deviceId}`, JSON.stringify(response.data)); // Cache successful fetch
            return response.data;
        } catch (error) {
            console.error('[userSlice] Error during backend fetch for user by deviceId:', error);
            if (axios.isAxiosError(error) && error.response?.status === 404) {
                console.log(`[userSlice] Backend returned 404 for deviceId: ${deviceId}. Checking cache.`);
                // Backend says no user. Check localStorage cache.
                const cachedUserJson = localStorage.getItem(`user_${deviceId}`);
                if (cachedUserJson) {
                    console.log(`[userSlice] Found cached user for deviceId ${deviceId}:`, cachedUserJson);
                    try {
                        const cachedUser = JSON.parse(cachedUserJson) as User;
                        // Basic validation of cached user object
                        if (cachedUser && cachedUser.id && cachedUser.name && cachedUser.deviceId === deviceId) {
                            console.log('[userSlice] Attempting to recreate user with cached data');
                            // Try to recreate the user in the backend
                            try {
                                const recreateResponse = await api.post<User>('/users', {
                                    name: cachedUser.name,
                                    deviceId: cachedUser.deviceId
                                });
                                console.log('[userSlice] Successfully recreated user:', recreateResponse.data);
                                localStorage.setItem(`user_${deviceId}`, JSON.stringify(recreateResponse.data));
                                return recreateResponse.data;
                            } catch (recreateError) {
                                console.error('[userSlice] Failed to recreate user:', recreateError);
                                // If recreation fails, still return the cached user
                                return cachedUser;
                            }
                        } else {
                            console.warn('[userSlice] Invalid cached user data. Removing from cache.', cachedUser);
                            localStorage.removeItem(`user_${deviceId}`); // Invalid cache
                            return null; // Proceed as if no user
                        }
                    } catch (e) {
                        console.error('[userSlice] Error parsing cached user. Removing from cache.', e);
                        localStorage.removeItem(`user_${deviceId}`); // Corrupted cache
                        return null; // Proceed as if no user
                    }
                }
                console.log(`[userSlice] No user on backend and no valid cache for deviceId: ${deviceId}.`);
                return null; // No user on backend, no valid cache
            }
            return rejectWithValue('Failed to initialize user');
        }
    }
);

export const setUserName = createAsyncThunk(
    'user/setName',
    async (name: string, { rejectWithValue }) => {
        const deviceId = localStorage.getItem('deviceId');
        if (!deviceId) {
            return rejectWithValue('Device ID not found. Cannot set user name.');
        }
        console.log(`[userSlice] Setting user name '${name}' for deviceId: ${deviceId}`);
        try {
            const response = await api.post<User>('/users', { name, deviceId });
            console.log('[userSlice] Backend response after setting name:', response.data);
            localStorage.setItem(`user_${deviceId}`, JSON.stringify(response.data)); // Cache after successful set
            localStorage.setItem('deviceId', deviceId);
            return response.data;
        } catch (error) {
            console.error('[userSlice] Error setting user name on backend:', error);
            if (axios.isAxiosError(error) && error.response) {
                return rejectWithValue(error.response.data?.message || `Failed to set user name: ${error.response.status}`);
            }
            return rejectWithValue('Failed to set user name due to an unexpected error');
        }
    }
);

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        clearUser: (state) => {
            const deviceId = localStorage.getItem('deviceId');
            if (deviceId) {
                console.log(`[userSlice] Clearing user and cache for deviceId: ${deviceId}`);
                localStorage.removeItem(`user_${deviceId}`);
            }
            state.currentUser = null;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(initializeUser.pending, (state) => {
                console.log('[userSlice] initializeUser.pending');
                state.loading = true;
                state.error = null;
            })
            .addCase(initializeUser.fulfilled, (state, action: PayloadAction<User | null>) => {
                console.log('[userSlice] initializeUser.fulfilled - action.payload:', action.payload);
                state.loading = false;
                state.currentUser = action.payload;
            })
            .addCase(initializeUser.rejected, (state, action) => {
                console.log('[userSlice] initializeUser.rejected - action:', action);
                console.log('[userSlice] initializeUser.rejected - payload:', action.payload);
                console.log('[userSlice] initializeUser.rejected - error:', action.error);
                state.loading = false;
                state.error = typeof action.payload === 'string' ? action.payload : action.error.message || 'Failed to initialize user';
            })
            .addCase(setUserName.pending, (state) => {
                console.log('[userSlice] setUserName.pending');
                state.loading = true;
                state.error = null;
            })
            .addCase(setUserName.fulfilled, (state, action: PayloadAction<User>) => {
                console.log('[userSlice] setUserName.fulfilled - action.payload:', action.payload);
                state.loading = false;
                state.currentUser = action.payload;
            })
            .addCase(setUserName.rejected, (state, action) => {
                console.log('[userSlice] setUserName.rejected - action:', action);
                state.loading = false;
                state.error = typeof action.payload === 'string' ? action.payload : action.error.message || 'Failed to set user name';
            });
    }
});

export const { clearUser } = userSlice.actions;
export default userSlice.reducer; 